import { Link } from "react-router-dom";
import { Package, Sparkles } from "lucide-react";

import { formatNumber } from "@/lib/format";
import { Badge } from "@/components/ui/Badge";
import type { Product } from "@/types/catalog";

// Custom Formatter for Tk
function formatPriceTk(amount: string | number) {
  const n = typeof amount === "string" ? Number(amount) : amount;
  const numStr = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(n) ? n : 0);
  return `Tk ${numStr}`;
}

export function ProductCard({ product }: { product: Product }) {
  const primary = product.images?.find((i) => i.is_primary) ?? product.images?.[0] ?? null;

  const price1 = Number(product.base_price) || 0;
  const price2 = product.compare_at_price ? Number(product.compare_at_price) : 0;

  let sellingPrice = price1;
  let crossedOutPrice = null;

  if (price2 > 0) {
    if (price2 > price1) {
      crossedOutPrice = price2;
      sellingPrice = price1;
    } else {
      crossedOutPrice = price1;
      sellingPrice = price2;
    }
  }

  return (
    // 🛠️ FIXED ROUTING: Changed to just `/catalog/${product.product_code}` to prevent layout issues
    <Link to={`/catalog/${product.product_code}`}
          className="group relative block bg-white rounded-[1.25rem] border border-surface-200/80 overflow-hidden shadow-sm hover:shadow-elevate-2 transition-all duration-300 hover:-translate-y-1 flex flex-col h-full z-10 hover:z-20">
      
      <div className="aspect-[4/5] bg-surface-50 relative overflow-hidden shrink-0 flex items-center justify-center p-2">
        {primary ? (
          <img src={primary.image} alt={product.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-700 bg-white rounded-lg" />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-surface-300 bg-white rounded-lg">
            <Package className="h-10 w-10 opacity-30" />
          </div>
        )}
        
        {/* Absolute Top Badges */}
        <div className="absolute top-2 left-2 flex flex-col gap-1.5 items-start">
          {crossedOutPrice && (
            <div className="bg-danger-600/95 backdrop-blur text-white text-[10px] px-2 py-0.5 rounded-md font-bold shadow-sm">
              SALE
            </div>
          )}
        </div>
      </div>
      
      <div className="p-3 sm:p-4 flex flex-col flex-1 bg-white relative z-10">
        <div className="text-[10px] uppercase tracking-wider text-surface-500 font-bold mb-1 truncate">
          {product.category?.name || "Uncategorized"}
        </div>
        <h3 className="font-bold text-surface-900 text-xs sm:text-sm line-clamp-2 leading-snug flex-1 group-hover:text-brand-600 transition-colors">{product.name}</h3>
        
        <div className="mt-3 sm:mt-4 pt-2 border-t border-surface-100/80 flex items-center justify-between min-h-[40px]">
          
          <div className="flex flex-col justify-center">
            {crossedOutPrice && (
              <div className="text-[10px] text-surface-400 line-through leading-none mb-0.5">
                {formatPriceTk(crossedOutPrice)}
              </div>
            )}
            <div className="text-xs sm:text-sm font-extrabold text-brand-700 leading-none tracking-tight">
              {formatPriceTk(sellingPrice)}
            </div>
          </div>
          
          {product.qualification_point_value > 0 && (
            <div className="flex items-center gap-1 text-brand-600 text-xs sm:text-sm font-extrabold leading-none">
              <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              {product.qualification_point_value} PV
            </div>
          )}
          
        </div>
      </div>
    </Link>
  );
}